import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AnswerButton } from "../button";
import "./Round.scss";
import useInterval from "../../hooks/useInterval";
import {
  MAX_QUESTION_SCORE,
  ROUND_DELAY,
  S3_BASE_URL,
  IMAGE_QUESION_DIR,
} from "../../config";
import classNames from "classnames";

interface answer {
  text: string;
  isCorrect: boolean;
}

interface round {
  answers: Array<answer>;
  questionText: string;
  questionTime: any;
  questionDifficulty: string | number;
  questionImageUrl: string;
}

interface RoundProps {
  roundData: round;
  timeLeft: number;
  isAnswered: boolean;
  scoreAnswer: Function;
  playerAnswer: string;
  questionScore?: number;
  pointsRemaining?: number;
  getScore: Function;
  questionNumber: number;
}

const roundVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: "circOut",
    },
  },
  exit: {
    opacity: 0,
  },
};

declare const window: any;

export const Question = ({
  roundData,
  timeLeft,
  isAnswered,
  scoreAnswer,
  playerAnswer,
  pointsRemaining,
  getScore,
  questionScore,
  questionNumber,
}: RoundProps) => {
  const { questionText, answers, questionImageUrl } = roundData;
  const [pointsLeft, setPointsLeft] = useState(pointsRemaining);

  useEffect(() => {
    window.bindTracking({
      type: "dynamic-page",
      data: {
        pageviewevent: 1,
        screenName: `Question ${questionNumber}`,
        screenSection: "Game",
        interactionName: "Question View",
        questionNumber,
        questionText,
        questionInteraction: "question viewed",
      },
    });
  }, [questionNumber]);

  useInterval(
    () => {
      setPointsLeft(getScore());
    },
    isAnswered || !timeLeft ? null : 60
  );

  function answerQuestion(a: answer) {
    if (!isAnswered) {
      window.bindTracking({
        type: "dynamic-page",
        data: {
          interactionEvent: 1,
          screenName: `Question ${questionNumber}`,
          screenSection: "Game",
          interactionName: "Question Answer",
          questionNumber,
          questionText,
          questionInteraction: a.isCorrect
            ? "question right"
            : "question wrong",
        },
      });
      scoreAnswer(a.text);
    }
  }

  const roundAnswers = answers.map((answer, i) => {
    const chosen = answer.text === playerAnswer;

    const getType = () => {
      if (isAnswered && answer.isCorrect) {
        return "correct";
      } else if (isAnswered && !answer.isCorrect && chosen) {
        return "wrong";
      } else if (isAnswered && !answer.isCorrect) {
        return "inactive";
      } else if (!timeLeft && !isAnswered) {
        return answer.isCorrect ? "correct" : "inactive";
      }
    };

    return (
      <AnswerButton
        onClick={() => answerQuestion(answer)}
        key={answer.text}
        type={getType()}
        chosen={answer.text === playerAnswer}
      >
        <div
          className={classNames({
            "answer-text": true,
            medium: answer.text.length <= 20,
            long: answer.text.length >= 21,
            extra: answer.text.length >= 45,
          })}
        >
          {answer.text}
        </div>
      </AnswerButton>
    );
  });

  function getPoints() {
    if (isAnswered) {
      return questionScore;
    } else {
      return !timeLeft && !isAnswered ? 0 : pointsLeft;
    }
  }


  const [showInterstitial, setShowInterstitial] = useState(false);

  // show interstitial when user answers
  // will disappear at end of round

  useEffect(() => {
    if (isAnswered && !showInterstitial) {
      setShowInterstitial(true);

      const id = setTimeout(() => {
        setShowInterstitial(false);
      }, timeLeft * 1000);

      return () => clearTimeout(id);
    }
  }, [isAnswered]);

  // show interstitial if time has run out and player hasn't answered
  useEffect(() => {
    if (!timeLeft && !isAnswered) {
      setShowInterstitial(true);

      const id = setTimeout(() => {
        setShowInterstitial(false);
      }, (ROUND_DELAY - 1) * 1000);

      return () => clearTimeout(id);
    }
  }, [timeLeft]);

  const getInterstitialMsg = () => {
    if (questionScore && isAnswered) {
      return (
        <div className="interstitial-correct">
          <span>Correct!!</span>
          <div>+ {questionScore} Points</div>
        </div>
      );
    } else if (!questionScore && isAnswered) {
      return "Incorrect!";
    } else if (!timeLeft && !isAnswered) {
      return (
        <div className="interstitial-time">
          <span>Uh, Hello?</span>
          <div>You forgot to answer the question...</div>
        </div>
      );
    }
  };


  let countdownBarWidth = ((getPoints() || MAX_QUESTION_SCORE / MAX_QUESTION_SCORE) - MAX_QUESTION_SCORE) / 10
        
  // make sure countdownbar reaches 0
  countdownBarWidth =  countdownBarWidth * 1.06


  const answerContainer = {
    hidden: { opacity: 1 },
    show: {
      opacity: 1,
      transition: {
        delayChildren: 0.4,
        staggerChildren: 0.15,
      },
    },
  };

  return (
    <>
      <motion.div key={questionText} className="tv-outer">
        <div className="tv" >
          <div className="tv-media">
            <AnimatePresence>
              {showInterstitial && getInterstitialMsg() && (
                <motion.div
                  className={classNames({
                    "interstitial-outer": true,
                    correct: questionScore && isAnswered,
                    wrong: !questionScore && isAnswered,
                  })}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="round-interstitial"
                    initial={{ y: 55, opacity: 0 }}
                    animate={{
                      y: 0,
                      opacity: 1,
                      transition: {
                        type: "tween",
                        duration: 0.3,
                        ease: "backOut",
                        delay: 0.15,
                      },
                    }}
                    exit={{ y: 55, opacity: 0 }}
                  >
                    {getInterstitialMsg()}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            <motion.img
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: {
                  delay: 0.15,
                  duration: 0.25,
                  ease: "circOut",
                },
              }}
              exit={{ opacity: 0 }}
              className="absolute w-full h-full cover relative tv-image"
              src={`${S3_BASE_URL}${IMAGE_QUESION_DIR}/${questionImageUrl}.jpg`}
              alt=""
            />
            <div className="countdown-bar relative">
                    <div className="countdown-bar-remaining-points">
                    {getPoints()} Points
                  </div>
              <div className="countdown-bar-fill-outer">
              <div
                className="countdown-bar-fill"
                style={{ transform: `translateX(${countdownBarWidth}%)` }}
              >
              </div>
              </div>

            </div>
          </div>
        </div>

        <motion.div
          variants={roundVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="question-panel"
        >
          <div
            className={classNames({
              question: true,
              medium: questionText.length >= 50,
              long: questionText.length >= 80,
            })}
          >
            {questionText}
          </div>
        </motion.div>
        <motion.div
          className="answers"
          variants={answerContainer}
          initial="hidden"
          animate="show"
          exit={{ opacity: 0, scale: 0 }}
        >
          {roundAnswers}
        </motion.div>
      </motion.div>
      {/* <AnimatePresence>
        {showInterstitial && getInterstitialMsg() && (
          <motion.div
            className="interstitial-outer"
            initial={{ scale: 0 }}
            animate={{
              scale: 1,
              transition: {
                type: "spring",
                stiffness: 275,
                damping: 10,
              },
            }}
            exit={{ scale: 0 }}
          >
            <motion.div
              className="round-interstitial"
              initial={{ scale: 0 }}
              animate={{
                scale: 1,
                transition: {
                  type: "spring",
                  stiffness: 275,
                  damping: 10,
                  delay: 0.15,
                },
              }}
              exit={{ scale: 0 }}
            >
              {getInterstitialMsg()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence> */}
    </>
  );
};

export default Question;
